/// <amd-module name="Build/Run/Components/IssuesCard" />
import * as React from "react";

import { IssueType, TaskResult, BuildResult, TimelineRecordState, BuildDefinition } from "Build/Client/Contracts/Build";
import { RunDetailsData, RunIssueData, RunIssueGroup } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { NavConstants } from "Build/Common/Library/Constants";
import { getTimelineStatusIndicatorData } from "Build/Common/Library/Indicator";
import { IssueAnalysisPanel, IIssueAnalysisPanelProps } from "Build/Run/Components/IssueAnalysisPanel";
import { PipelinePermissionsErrorCard } from "Build/Run/Components/PipelinePermissionsErrorCard";
import * as Resources from "Build/Run/Resources";
import { IRunDetailsService } from "Build/Run/RunDetailsService.types";

import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";

import { IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext, IVssComponentProperties, VssComponent, css, IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssContributionService } from "VSS/Platform/Contribution";

import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Status, StatusSize } from "VSSUI/Status";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Table, TableRow, ITableColumn, ITableRowDetails, SimpleTableCell, ITableRow, TwoLineTableCell, IMeasurementStyle, TableCell } from "VSSUI/Table";
import { Tab, TabBar, TabContent, TabProvider, TabSize } from "VSSUI/Tabs";
import { Tooltip } from "VSSUI/TooltipEx";

const yamlAuthorizationErrorUrl = "https://aka.ms/yamlauthz";
const issuesCardLinkProviderType = "ms.vss-build-web.build-results-issues-card-link-provider";

interface IIssuesCardState {
    run: RunDetailsData;
    issues: RunIssueGroup[];
    resourceAuthErrors: RunIssueData[];
    maxVisibleWarnings: number;
    maxVisibleErrors: number;
}

interface IIssueRow extends RunIssueData {
    isShowMoreRow?: boolean;
    groupName: string;
    renderRow?: (rowIndex: number, item: RunIssueData, details: ITableRowDetails<RunIssueData>) => JSX.Element;
}

interface IContributedIssuesCardLink {
    url: string;
    displayValue: string;
}

interface IIssuesCardLinkProvider {
    id: string;
    contribution: any;
}

interface IIssuesCardGetLinkParams {
    runId: number;
}

interface IIssuesCardLinkService {
    getLink: (params: IIssuesCardGetLinkParams) => Promise<IContributedIssuesCardLink | null>;
}

namespace Tabs {
    export const Errors = "errors";
    export const Warnings = "warnings";
}

export class IssuesCard extends VssComponent<IVssComponentProperties, IIssuesCardState> {
    private _service: IRunDetailsService;
    private _observable: IReadonlyObservableValue<RunDetailsData>;
    private _selectedTabId: ObservableValue<string>;

    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        this._service = this.context.pageContext.getService<IRunDetailsService>("IRunDetailsService");
        this._observable = this._service.data;

        const run = this._service.data.value;
        this._selectedTabId = new ObservableValue(Tabs.Errors);
        this.state = this._createNewState(run);
    }

    public render() {
        if (this.state.resourceAuthErrors && this.state.resourceAuthErrors.length > 0) {
            return (
                <div>
                    {this._getResourceAuthErrorCard(this.state.resourceAuthErrors)}
                </div>
            )
        }
        else if (this.state.issues && this.state.issues.length > 0) {
            return (
                <div>
                    {this._getGeneralIssueCard(this.state.issues)}
                </div>
            );
        }
        else {
            return null;
        }
    }

    public componentDidMount() {
        this._observable.subscribe(this._onObservableChanged);
    }

    public componentWillUnmount() {
        this._observable.unsubscribe(this._onObservableChanged);
    }

    private _createNewState(run: RunDetailsData): IIssuesCardState {
        const newState: IIssuesCardState = {
            run: run,
            issues: [],
            resourceAuthErrors: [],
            maxVisibleWarnings: 10,
            maxVisibleErrors: 10
        };

        // Separate resource auth errors from other issues this way until we properly classify errors
        (run.issues || []).forEach(issueGroup => {
            (issueGroup.issues || []).forEach((issue) => {
                if (issue.type === IssueType.Error && issue.message && issue.message.indexOf(yamlAuthorizationErrorUrl) > 0) {
                    newState.resourceAuthErrors.push(issue);
                }
            });

            newState.issues.push(issueGroup);
        });

        return newState;
    }

    private _onObservableChanged = (data: RunDetailsData) => {
        this.setState(this._createNewState(data));
    }

    private _getResourceAuthErrorCard(resourceAuthErrors: RunIssueData[]) {
        if (resourceAuthErrors && resourceAuthErrors.length > 0) {
            return (
                <PipelinePermissionsErrorCard
                    resourceAuthErrorMessages={this._getTopIssue(resourceAuthErrors).message}
                    pipelineId={this.state.run.pipeline.id}
                    sourceBranch={this.state.run.sourceBranch} />
            );
        }
        else {
            return null;
        }
    }

    private _getAdditionalLinks(runId: number) {
        const additionalLinks = new ObservableArray<IContributedIssuesCardLink>();

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const allProviders = contributionService.getContributionsByType<IIssuesCardLinkProvider>(issuesCardLinkProviderType);

        for (const provider in allProviders) {
            contributionService.getService<IIssuesCardLinkService>(provider).then(issuesCardLinkService => {
                if (issuesCardLinkService) {
                    const link = issuesCardLinkService.getLink({ runId });
                    if (link && typeof link.then === "function") {
                        link.then(
                            resolvedLink => {
                                if (resolvedLink) {
                                    additionalLinks.push(resolvedLink);
                                }
                            },
                            (e: Error) => {
                                const message = `Failed to load additional links for the Issues Card from ${provider} : ${JSON.stringify(e || "")}`;
                                console.warn(message);
                            }
                        );
                    }
                }
            });
        }

        return additionalLinks;
    }

    private _getGeneralIssueCard(issues: RunIssueGroup[]) {
        if (issues && issues.length > 0) {
            const totalErrorCount = this.state.run.errorCount;
            const totalWarningCount = this.state.run.warningCount;

            if (totalErrorCount === 0) {
                this._selectedTabId.value = Tabs.Warnings;
            }
            else if (totalWarningCount === 0) {
                this._selectedTabId.value = Tabs.Errors;
            }

            return (
                <Card className="margin-top-16 flex-grow bolt-table-card">
                    <Surface background={SurfaceBackground.normal}>
                        <Observer selectedTab={this._selectedTabId}>
                            {(props: { selectedTab: string }) => {
                                return (
                                    <TabProvider selectedTabId={props.selectedTab} providers={new ObservableArray([])}>
                                        <div className="issues-card-content flex-column padding-top-16">
                                            <TabBar className="padding-bottom-16" onSelectedTabChanged={this._onTabChanged} disableSticky={true} tabSize={TabSize.LargeLink}>
                                                {totalErrorCount > 0 && <Tab id={Tabs.Errors} key={Tabs.Errors} name={Resources.Errors} badgeCount={totalErrorCount} />}
                                                {totalWarningCount > 0 && <Tab id={Tabs.Warnings} key={Tabs.Warnings} name={Resources.Warnings} badgeCount={totalWarningCount} />}
                                            </TabBar>
                                            <TabContent>
                                                <div className="flex-column">
                                                    {
                                                        this._getIssueTableForGroup(issues, props.selectedTab === Tabs.Errors ? IssueType.Error : IssueType.Warning)
                                                    }
                                                    {
                                                        this.state.run.result === BuildResult.Failed &&
                                                        <div className="flex-row flex-grow justify-end">
                                                            <Observer items={this._getAdditionalLinks(this.state.run.id)}>
                                                                {(observedProps: { items: IContributedIssuesCardLink[] }) => {
                                                                    return observedProps.items.length > 0
                                                                        ? observedProps.items.map(item => <Link className="margin-16" href={item.url} target="_blank">{item.displayValue}</Link>)
                                                                        : null;
                                                                }}
                                                            </Observer>
                                                            <Link className="margin-16" href="https://go.microsoft.com/fwlink/?linkid=2101318" target="_blank">{Resources.Troubleshoot}</Link>
                                                        </div>
                                                    }
                                                </div>
                                            </TabContent>
                                        </div>
                                    </TabProvider>
                                );
                            }}
                        </Observer>
                    </Surface>
                </Card>
            );
        }
        else {
            return null;
        }
    }

    private _getIssueTableForGroup = (issues: RunIssueGroup[], issueType: IssueType) => {
        let visibleIssueCount = 0;
        let visibleAdditionalIssueCount = 0;

        const allIssues = issues.reduce((issuesToShow: IIssueRow[], group: RunIssueGroup) => {
            const issuesFromCurrentTaskToDisplay: number = (issueType === IssueType.Error
                ? this.state.maxVisibleErrors
                : this.state.maxVisibleWarnings) - visibleIssueCount;

            if (!group || !group.issues || issuesFromCurrentTaskToDisplay <= 0) {
                return issuesToShow;
            }

            const groupName = this._getGroupName(group);

            const filteredIssues = group.issues.filter((issue: RunIssueData) => issue.type === issueType);

            const issues: IIssueRow[] = filteredIssues
                .slice(0, issuesFromCurrentTaskToDisplay)
                .map((issue: RunIssueData) => ({ ...issue, groupName }));

            if (issues.length === 0) {
                return issuesToShow;
            }

            visibleIssueCount += issues.length;

            if (filteredIssues.length === issues.length) {
                const additionalIssueCount = (issueType === IssueType.Error ? group.additionalErrorCount : group.additionalWarningCount);

                if (additionalIssueCount > 0) {
                    issues.push({
                        stageId: group.stageId,
                        stageName: group.stageName,
                        jobId: group.jobId,
                        jobName: group.jobName,
                        taskId: group.taskId,
                        taskName: group.taskName,
                        type: issueType,
                        message: format(issueType == IssueType.Error ? Resources.ViewLogForMoreErrors : Resources.ViewLogForMoreWarnings,
                            additionalIssueCount),
                        renderRow: this._renderAdditionalIssuesRow
                    } as IIssueRow);
                }

                visibleAdditionalIssueCount += additionalIssueCount;
            }

            return issuesToShow.concat(issues);
        }, []);

        const totalIssueCount = issueType === IssueType.Error ? this.state.run.errorCount : this.state.run.warningCount;
        const totalAccountedForIssueCount = visibleIssueCount + visibleAdditionalIssueCount;

        if (totalAccountedForIssueCount < totalIssueCount) {
            allIssues.push({
                isShowMoreRow: true,
                type: issueType,
                message: format(Resources.ShowAdditionalIssues, totalIssueCount - totalAccountedForIssueCount, issueType === IssueType.Error ? "errors" : "warnings"),
                renderRow: this._renderAdditionalIssuesRow,
            } as IIssueRow);
        }

        const itemProvider = new ObservableArray(allIssues);

        const columns: ITableColumn<IIssueRow>[] = [
            {
                id: "issue-column",
                width: -100,
                renderCell: this._renderIssue
            },
            {
                id: "analysis-column",
                width: new ObservableValue(2.625),
                widthStyle: IMeasurementStyle.REM,
                className: "pipelines-cell",
                renderCell: this._renderAnalysisCell,
                ariaLabel: Resources.Analysis
            },
        ];

        return (
            <Table ariaLabel={Resources.Issues} className="issue-table-border" columns={columns} key={issueType} itemProvider={itemProvider} onActivate={this._onActivateRow} showHeader={false} selectableText={true} />
        );
    }

    private _onTabChanged = (tabKey: string) => {
        this._selectedTabId.value = tabKey;
    }

    private _getGroupName(group: RunIssueGroup) {
        const showStage: boolean = this.state.run.stages.length > 1;
        const showJob: boolean = this.state.run.jobs.length > 1;
        let groupName = this.state.run.number;
        if (group.taskName) {
            if (showJob) {
                if (showStage) {
                    groupName = format("{0} • {1} • {2}", group.stageName, group.jobName, group.taskName);
                }
                else {
                    groupName = format("{0} • {1}", group.jobName, group.taskName);
                }
            }
            else {
                groupName = group.taskName;
            }
        }
        else if (group.jobName && showJob) {
            if (showStage) {
                groupName = format("{0} • {1}", group.stageName, group.jobName);
            }
            else {
                groupName = group.jobName;
            }
        }
        else if (group.stageName && showStage) {
            groupName = group.stageName;
        }
        return groupName;
    }

    private _getTopIssue(issues: RunIssueData[]): RunIssueData {
        // Use the first error in the list of issues, prioritize failed jobs
        // Or, if there are no errors, use the first issue in the list
        let topIssueId = 0;
        for (let i = 0; i < issues.length; i++) {
            const issue = issues[i];
            if (issue.type === IssueType.Error && issue.jobResult === TaskResult.Failed) {
                topIssueId = i;
                break;
            }
        }

        return issues[topIssueId];
    }

    private _renderAdditionalIssuesRow = (rowIndex: number, item: IIssueRow, details: ITableRowDetails<IIssueRow>): JSX.Element => {
        return (
            <TableRow details={details} index={rowIndex} className="view-more-issues-row" key={"row-" + rowIndex}>
                <SimpleTableCell columnIndex={0} tableColumn={undefined}>
                    <div className={"view-more-issues-text" + (item.isShowMoreRow ? " center" : "")}>
                        {item.message}
                    </div>
                </SimpleTableCell>
            </TableRow>
        );
    }

    private _renderAnalysisCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<RunIssueData>, tableItem: RunIssueData): JSX.Element => {
        // tableItem.jobId will exist for all errors except errors that occur while sending or during yaml parse. don't show the button if there is no task associated with error
        return isFeatureFlagEnabled(this.context.pageContext, "Build2.ErrorAnalysisPanel", false) && (tableItem.jobId && tableItem.taskName && tableItem.taskType) ? (
            <TableCell className="bolt-table-cell-side-action run-analysis-cell" columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className={css("flex-column", "padding-right-4")}>
                    <div className="right-align flex-row">
                        <Button
                            className="bolt-table-cell-side-action bolt-table-cell-content-reveal"
                            primary={false}
                            subtle={true}
                            iconProps={{ iconName: "Flashlight" }}
                            ariaLabel={Resources.Analysis}
                            onClick={(event) => {
                                event.preventDefault();
                                this._renderIssueAnalysisPanel(tableItem);
                            }}
                            excludeTabStop={true} />
                    </div>
                </div>
            </TableCell>
        ) : <div></div>;
    }

    private _renderIssueAnalysisPanel = (issue: RunIssueData) => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss) => {
            const props: IIssueAnalysisPanelProps = {
                run: this.state.run,
                issue,
                pageContext: this.context.pageContext,
                onDismiss
            };
            return React.createElement(IssueAnalysisPanel, {
                ...props,
            }, null);
        });
    }

    private _renderIssue = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IIssueRow>, tableItem: IIssueRow): JSX.Element => {
        let firstLine: string = tableItem.message;

        if (firstLine === undefined || firstLine.length === 0) {
            firstLine = Resources.NoMessageForIssue;
        }

        const statusData = getTimelineStatusIndicatorData(TimelineRecordState.Completed, tableItem.type === IssueType.Error ? TaskResult.Failed : TaskResult.SucceededWithIssues);

        const firstLineComponent = <Tooltip overflowOnly={true} text={firstLine}>
            <span className="body-m text-ellipsis">{firstLine}</span>
        </Tooltip>
        const statusIndicator = <Status {...statusData.statusProps} size={StatusSize.m} className="margin-right-8" />;

        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                iconProps={{ render: () => statusIndicator }}
                key={"col-" + columnIndex + " row-" + rowIndex}
                line1={firstLineComponent}
                line2={
                    <span className="body-s secondary-text text-ellipsis">
                        {tableItem.groupName}
                    </span>
                } />
        );
    }

    private _onActivateRow = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, tableRow: ITableRow<IIssueRow>) => {
        if (tableRow.data && tableRow.data.jobId) {
            const jobRoutes: { [key: string]: string } = {};
            jobRoutes[NavConstants.job] = tableRow.data.jobId;
            if (tableRow.data.taskId) {
                jobRoutes[NavConstants.task] = tableRow.data.taskId;
            }
            if (tableRow.data.lineNumber) {
                jobRoutes[NavConstants.line] = tableRow.data.lineNumber.toString();
            }
            const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
            const jobUrl = linkingService.getBuildUrl(this.state.run.id, NavConstants.logs, undefined, undefined, jobRoutes);

            onClickFPS(this.context.pageContext, jobUrl, true, event);
        }
        else if (tableRow.data.isShowMoreRow && tableRow.data) {
            if (tableRow.data.type === IssueType.Error) {
                this.setState({ maxVisibleErrors: this.state.maxVisibleErrors + 10 });
            }
            else {
                this.setState({ maxVisibleWarnings: this.state.maxVisibleWarnings + 10 });
            }
        }
        else {
            const toastService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
            toastService.addToast({
                duration: 2000,
                message: Resources.NoLogsToastMessage
            });
        }
    }
}
